import React, { useState, useEffect } from 'react';
import Button from './Button';
import InputField from './InputField';
import type { CharacterStats } from '../../types/character';
import { CHARACTER_STAT_LABELS, CHARACTER_STAT_KEYS } from '../../types/character';
import {
  normalizeCharacterStats,
  normalizeCharacterLevel,
  CHARACTER_STAT_MAX,
  CHARACTER_STAT_MIN,
  CHARACTER_LEVEL_MIN,
  CHARACTER_LEVEL_MAX,
} from '../../utils/characterStats';
import { DEFAULT_CHARACTER_LEVEL } from '../../types/character';

interface EditStudentInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  studentNumber: number;
  studentPoints: number;
  /** 학생 문서의 캐릭터 능력치(없으면 기본값) */
  characterStats?: CharacterStats;
  characterLevel?: number;
  onSave: (
    name: string,
    number: number,
    points: number,
    characterStats: CharacterStats,
    characterLevel: number
  ) => void;
  scale?: number;
}

const EditStudentInfoDialog: React.FC<EditStudentInfoDialogProps> = ({
  isOpen,
  onClose,
  studentName,
  studentNumber,
  studentPoints,
  characterStats: characterStatsProp,
  characterLevel: characterLevelProp,
  onSave,
  scale = 1
}) => {
  const [name, setName] = useState(studentName);
  const [number, setNumber] = useState(studentNumber);
  const [points, setPoints] = useState(studentPoints);
  const [stats, setStats] = useState<CharacterStats>(() => normalizeCharacterStats(characterStatsProp));
  const [level, setLevel] = useState(() => normalizeCharacterLevel(characterLevelProp));
  const [nameError, setNameError] = useState('');

  // 다이얼로그가 열릴 때마다 초기값 설정
  useEffect(() => {
    if (isOpen) {
      setName(studentName);
      setNumber(studentNumber);
      setPoints(studentPoints);
      setStats(normalizeCharacterStats(characterStatsProp));
      setLevel(normalizeCharacterLevel(characterLevelProp));
      setNameError('');
    }
  }, [isOpen, studentName, studentNumber, studentPoints, characterStatsProp, characterLevelProp]);

  const setStat = (key: keyof CharacterStats, raw: number) => {
    const n = Number.isFinite(raw) ? Math.round(raw) : CHARACTER_STAT_MIN;
    const v = Math.min(CHARACTER_STAT_MAX, Math.max(CHARACTER_STAT_MIN, n));
    setStats((prev) => ({ ...prev, [key]: v }));
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('학생 이름을 입력해주세요.');
      return;
    }
    setNameError('');
    onSave(trimmed, number, points, normalizeCharacterStats(stats), normalizeCharacterLevel(level));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      boxSizing: 'border-box',
      fontFamily: 'Pretendard',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--Color-background-normal, white)',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: 'calc(100vh - 40px)',
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transformOrigin: 'center center',
        transform: `scale(${scale})`,
        padding: '24px 28px',
        gap: '24px',
        boxSizing: 'border-box'
      }}>
        {/* 상단 영역 (스크롤) */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px',
            flexShrink: 0
          }}>
            <div style={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                lineHeight: '30px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                학생 정보 수정하기
              </h1>
              <span style={{
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '24px',
                color: 'var(--Color-text-assistive, #989898)',
                wordWrap: 'break-word'
              }}>
                학생의 정보 수정이 필요하신가요?
              </span>
            </div>
            
            {/* 닫기 버튼 */}
            <div style={{
              padding: '4px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
            onClick={onClose}
            >
              <div style={{
                width: '24px',
                height: '24px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src="/images/icons/closeIcon.svg" 
                  alt="닫기"
                  style={{
                    width: '13.20px',
                    height: '13.20px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* 입력 필드 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {/* 학생 이름 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <label style={{
                fontSize: '16px',
                fontWeight: '600',
                lineHeight: '24px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                학생 이름
              </label>
              <InputField
                value={name}
                onChange={setName}
                placeholder="이름을 입력하세요"
                size="medium"
              />
              {nameError ? (
                <span style={{
                  fontSize: '13px',
                  color: 'var(--Color-status-negative, #E53935)',
                  lineHeight: '19.5px'
                }}>
                  {nameError}
                </span>
              ) : null}
            </div>

            {/* 학번 & 포인트 */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '24px',
              flexWrap: 'wrap'
            }}>
              {/* 학번 */}
              <div style={{
                width: '165px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  lineHeight: '24px',
                  color: 'var(--Color-text-normal, #161616)',
                  wordWrap: 'break-word'
                }}>
                  학번
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <input
                    type="number"
                    value={number}
                    onChange={(e) => setNumber(Number(e.target.value))}
                    style={{
                      width: '129px',
                      height: '48px',
                      padding: '12px 16px',
                      background: 'var(--Color-fill-white, white)',
                      borderRadius: '8px',
                      border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '13px',
                      fontWeight: '400',
                      lineHeight: '19.5px',
                      color: 'var(--Color-text-normal, #161616)',
                      outline: 'none',
                      fontFamily: 'Pretendard'
                    }}
                  />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    lineHeight: '24px',
                    color: 'var(--Color-text-normal, #161616)',
                    wordWrap: 'break-word'
                  }}>
                    번
                  </span>
                </div>
              </div>

              {/* 포인트 */}
              <div style={{
                width: '205px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  lineHeight: '24px',
                  color: 'var(--Color-text-normal, #161616)',
                  wordWrap: 'break-word'
                }}>
                  포인트
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <input
                    type="number"
                    value={points}
                    onChange={(e) => setPoints(Number(e.target.value))}
                    style={{
                      width: '179px',
                      height: '48px',
                      padding: '12px 16px',
                      background: 'var(--Color-fill-white, white)',
                      borderRadius: '8px',
                      border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '13px',
                      fontWeight: '400',
                      lineHeight: '19.5px',
                      color: 'var(--Color-text-normal, #161616)',
                      outline: 'none',
                      fontFamily: 'Pretendard'
                    }}
                  />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    lineHeight: '24px',
                    color: 'var(--Color-text-normal, #161616)',
                    wordWrap: 'break-word'
                  }}>
                    점
                  </span>
                </div>
              </div>
            </div>

            {/* 캐릭터 레벨 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <label style={{
                fontSize: '16px',
                fontWeight: '600',
                lineHeight: '24px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                캐릭터 레벨
              </label>
              <span style={{
                fontSize: '13px',
                fontWeight: '500',
                lineHeight: '19.5px',
                color: 'var(--Color-text-assistive, #989898)',
              }}>
                Lv.{CHARACTER_LEVEL_MIN} ~ Lv.{CHARACTER_LEVEL_MAX}
              </span>
              <input
                type="number"
                min={CHARACTER_LEVEL_MIN}
                max={CHARACTER_LEVEL_MAX}
                value={level}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  const n = Number.isFinite(raw) ? Math.round(raw) : DEFAULT_CHARACTER_LEVEL;
                  setLevel(Math.min(CHARACTER_LEVEL_MAX, Math.max(CHARACTER_LEVEL_MIN, n)));
                }}
                style={{
                  width: '100%',
                  maxWidth: '160px',
                  height: '48px',
                  padding: '12px 16px',
                  background: 'var(--Color-fill-white, white)',
                  borderRadius: '8px',
                  border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                  fontSize: '13px',
                  fontWeight: '400',
                  lineHeight: '19.5px',
                  color: 'var(--Color-text-normal, #161616)',
                  outline: 'none',
                  fontFamily: 'Pretendard',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* 캐릭터 능력치 (1~10) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <label style={{
                fontSize: '16px',
                fontWeight: '600',
                lineHeight: '24px',
                color: 'var(--Color-text-normal, #161616)',
                wordWrap: 'break-word'
              }}>
                캐릭터 능력치
              </label>
              <span style={{
                fontSize: '13px',
                fontWeight: '500',
                lineHeight: '19.5px',
                color: 'var(--Color-text-assistive, #989898)',
              }}>
                ATK · DEF · HP · MP (각 {CHARACTER_STAT_MIN}~{CHARACTER_STAT_MAX})
              </span>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
              }}>
                {CHARACTER_STAT_KEYS.map((key) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '120px' }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--Color-text-normal, #161616)',
                    }}>
                      {CHARACTER_STAT_LABELS[key]}
                    </span>
                    <input
                      type="number"
                      min={CHARACTER_STAT_MIN}
                      max={CHARACTER_STAT_MAX}
                      value={stats[key]}
                      onChange={(e) => setStat(key, Number(e.target.value))}
                      style={{
                        width: '100%',
                        maxWidth: '120px',
                        height: '48px',
                        padding: '12px 16px',
                        background: 'var(--Color-fill-white, white)',
                        borderRadius: '8px',
                        border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                        fontSize: '13px',
                        fontWeight: '400',
                        lineHeight: '19.5px',
                        color: 'var(--Color-text-normal, #161616)',
                        outline: 'none',
                        fontFamily: 'Pretendard',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0
        }}>
          <Button
            width="124px"
            height="45px"
            variant="cancel"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            width="262px"
            height="45px"
            variant="submit"
            onClick={handleSave}
          >
            수정
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditStudentInfoDialog;

